import React, { useState, useEffect, memo } from "react";
import { backend, API_URL } from "../../../config";
import {
  CircularProgress,
  Container,
  TableContainer,
  Table,
  TableHead,
  TableCell,
  TableRow,
  TableBody,
  Typography,
  Button,
  Link,
  Tooltip,
  IconButton,
  Stack,
  Modal,
  Grid,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: "relative",
  },
  layout: {
    width: "auto",
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(2),
    [theme.breakpoints.up(600)]: {
      width: 900,
      marginLeft: "auto",
      marginRight: "auto",
    },
  },
  paper: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
    padding: theme.spacing(5),
    [theme.breakpoints.up(600 + theme.spacing(3) * 2)]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3),
    },
  },
  stepper: {
    padding: theme.spacing(3, 0, 5),
  },
  buttons: {
    display: "flex",
    justifyContent: "flex-end",
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
  modal: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
}));

export const SaleTaxRate = (props) => {
  const classes = useStyles();

  const [errorInfo, setErrorInfo] = useState({ error: false, message: "" });
  const [companyLocations, setCompanyLocations] = useState([]);
  const [companyLocationsLoaded, setCompanyLocationsLoaded] = useState(false);
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  const handleChangeIncludeInactive = (e) => {
    const { checked } = e.target;

    setIncludeInactive(checked);
    setCompanyLocationsLoaded(false);
  };

  const updateModal = selectedItem !== null;

  const resolveRequest = () => {
    setLoadingFlag(false);
    setFinishedFlag(false);
    setSelectedItem(null);
    setErrorInfo({ error: false, message: "" });
  };

  const sendRequest = async () => {
    setLoadingFlag(true);

    const companyLocationObj = {
      sales_tax_rate: selectedItem.cdtfa_sales_tax_rate,
    };

    await backend
      .patch(
        `${API_URL}companylocations/${selectedItem.id}`,
        companyLocationObj,
      )
      .then(({ data }) => {
        setCompanyLocations((prevState) =>
          prevState.map((item) => {
            if (item.id !== selectedItem.id) return item;

            return {
              ...item,
              ...data,
              cdtfa_sales_tax_rate: data.sales_tax_rate,
              updated_sales_tax_rate: true,
            };
          }),
        );
        setFinishedFlag(true);
        setLoadingFlag(false);
      })
      .catch((error) => {
        console.log(error);
        setErrorInfo({
          error: true,
          message: JSON.stringify(error.response.data),
        });
      });

    resolveRequest();
  };

  const updateModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <>
          Are you sure you want to update
          <b>{` ${selectedItem?.name || selectedItem?.api_description} `}</b>{" "}
          sales tax rate?
          <br />
          <br />
          <div align="right">
            <Button onClick={() => resolveRequest()} className={classes.button}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => sendRequest()}
              className={classes.button}
            >
              Confirm
            </Button>
          </div>
        </>
      ) : loadingFlag ? (
        <>
          <div align="center">
            <CircularProgress />
            <div>
              Reset overrides in progress
              <br />
              This may take some time relative to the number of overrides
            </div>
          </div>
          {errorInfo.error ? (
            <>
              <br />
              <br />
              There was an error while resetting overrides
              <br />
              <br />
              <span
                style={{
                  color: "red",
                  wordBreak: "break-word",
                  maxHeight: 160,
                  overflow: "scroll",
                  display: "inline-block",
                  width: "100%",
                }}
              >
                <b>Error:</b>
                <br />
                <div dangerouslySetInnerHTML={{ __html: errorInfo.message }} />
              </span>
              <div align="right">
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => resolveRequest()}
                  className={classes.button}
                >
                  Continue
                </Button>
              </div>
            </>
          ) : (
            <></>
          )}
        </>
      ) : (
        <>
          The overrides reset was successful.
          <div align="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => resolveRequest()}
              className={classes.button}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );

  useEffect(() => {
    const getCompanyLocationSaleTaxRate = async (dataMap) => {
      const clCDTFAMap = dataMap;

      Promise.all(
        clCDTFAMap.map((item) =>
          backend
            .get(`${API_URL}companylocations/${item.id}/saletaxrate`)
            .then(({ data }) => {
              setCompanyLocations((prevState) =>
                prevState.map((clItem) => {
                  if (clItem.id !== item.id) return clItem;
                  return {
                    ...clItem,
                    cdtfa_response: data,
                    cdtfa_sales_tax_rate: data?.taxRateInfo?.[0]?.rate
                      ? data.taxRateInfo[0].rate * 100
                      : null,
                  };
                }),
              );
            })
            .catch((error) => {
              console.log(error);
              setCompanyLocations((prevState) =>
                prevState.map((clItem) => {
                  if (clItem.id !== item.id) return clItem;

                  return {
                    ...clItem,
                    cdtfa_sales_tax_rate: null,
                    cdtfa_response: error.response?.data || null,
                    updated_sales_tax_rate: false,
                  };
                }),
              );
            }),
        ),
      ).then(() => setCompanyLocationsLoaded(true));
    };

    const getCompanyLocations = async () => {
      let urlParam = "no_page=1";

      return await backend
        .get(`${API_URL}companylocations?${urlParam}`)
        .then(({ data }) => {
          let dataMap = data.map((item, index) => ({
            ...item,
            cdtfa_sales_tax_rate: null,
            cdtfa_response: null,
            updated_sales_tax_rate: false,
          }));

          // filter out inactive company locations if includeInactive is false
          if (!includeInactive) {
            dataMap = dataMap.filter((item) => item.active);
          }

          // filter out company locations with testing addresses
          dataMap = dataMap.filter((item) => {
            const hasBillingAddress = Boolean(item?.billing_address);
            const hasTestWordInAddress =
              Boolean(
                item?.billing_address?.address_line1
                  ?.toLowerCase()
                  .includes("test"),
              ) ||
              Boolean(
                item?.billing_address?.address_line2
                  ?.toLowerCase()
                  .includes("test"),
              );

            return hasBillingAddress && !Boolean(hasTestWordInAddress);
          });

          setCompanyLocations(dataMap);
          getCompanyLocationSaleTaxRate(dataMap);
        });
    };

    if (!companyLocationsLoaded) {
      getCompanyLocations();
    }
  }, [companyLocationsLoaded, includeInactive]);

  return (
    <Container>
      <Modal open={updateModal} onClose={resolveRequest}>
        {updateModalContent}
      </Modal>
      {!companyLocationsLoaded && (
        <div align="center">
          <br />
          <CircularProgress />
          <div>
            Loading company locations <br />
          </div>
        </div>
      )}
      {companyLocationsLoaded && !companyLocations.length && (
        <div align="center">
          <br />
          <InfoOutlinedIcon fontSize="large" />
          <div>
            There are no company locations to display. <br />
          </div>
        </div>
      )}
      {!errorInfo.error && companyLocationsLoaded && companyLocations.length ? (
        <>
          <Grid container spacing={2}>
            <Grid item xs={true}>
              <Typography component="h6" variant="h6">
                {`Company Locations`}
              </Typography>
            </Grid>
            <Grid item xs={"auto"}>
              <FormControlLabel
                value="start"
                control={
                  <Checkbox
                    name="include_inactive"
                    value={includeInactive}
                    checked={includeInactive}
                    onChange={handleChangeIncludeInactive}
                    color="primary"
                  />
                }
                label="Show Inactive"
                labelPlacement="end"
              />
            </Grid>
          </Grid>
          <TableComponent
            companyLocations={companyLocations}
            setSelectedItem={setSelectedItem}
          />
        </>
      ) : (
        <></>
      )}
    </Container>
  );
};

const TableComponent = memo(({ companyLocations, setSelectedItem }) => {
  return (
    <TableContainer style={{ maxHeight: "80vh" }}>
      <Table stickyHeader aria-label="sticky table">
        <TableHead>
          <TableRow>
            <TableCell size="small">Name</TableCell>
            <TableCell size="small">Sale Tax Rate</TableCell>
            <TableCell size="small">CDTFA Sale Tax Rate</TableCell>
            <TableCell size="small">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {companyLocations.map((item, index) => (
            <TableRowComponent
              key={index}
              item={item}
              setSelectedItem={setSelectedItem}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

const TableRowComponent = memo(({ item, index, setSelectedItem }) => {
  return (
    <TableRow
      key={`row-${index}`}
      hover
      tabIndex={-1}
      style={{ opacity: item.updated_sales_tax_rate ? 0.5 : 1 }}
    >
      <TableCell size="small">{item.name}</TableCell>
      <TableCell size="small" style={{ textAlign: "center" }}>
        {item.sales_tax_rate ? `${item.sales_tax_rate}` : "N/A"}
      </TableCell>
      <TableCell
        size="small"
        style={{
          textAlign: "center",
          background:
            item.cdtfa_sales_tax_rate !== null &&
            item.sales_tax_rate !== item.cdtfa_sales_tax_rate
              ? "lightyellow"
              : "inherit",
        }}
      >
        {item.cdtfa_sales_tax_rate ? `${item.cdtfa_sales_tax_rate}` : "N/A"}
      </TableCell>
      <TableCell size="small">
        <Stack direction="row" spacing={1}>
          <Link href={`/customer/${item.id}`} target="_blank">
            <Tooltip title="Edit customer location">
              <IconButton aria-label="edit customer location">
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          </Link>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={() => setSelectedItem(item)}
            disabled={
              item.cdtfa_sales_tax_rate === null ||
              (!item.sales_tax_rate && !item.cdtfa_sales_tax_rate) ||
              item.sales_tax_rate === item.cdtfa_sales_tax_rate ||
              item.updated_sales_tax_rate
            }
          >
            <Typography variant="subtitle2" className="text-white">
              Update
            </Typography>
          </Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
});

export default SaleTaxRate;
